import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  CalendarIcon,
  MapPin,
  Star,
  Share2,
  Heart,
  Wifi,
  ParkingCircle,
  Coffee,
  Tv,
  Snowflake,
  Music2,
  Users,
  ShieldCheck,
  Plus,
  Minus,
  Clock,
  ChevronLeft,
  Maximize2,
  DoorOpen,
  Ruler,
  Navigation,
  BadgePercent,
  ArrowRight,
  Sparkles,
  Check,
  CircleCheck,
  Store,
  UtensilsCrossed,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import logo from "@/assets/company-logo.webp";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useFavorite, shareSpace } from "@/lib/favorites-store";
import { useHasBooked, useSpaceReviews, addReview } from "@/lib/reviews-store";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import s1 from "@/assets/space-1.webp";
import s2 from "@/assets/space-2.webp";
import s3 from "@/assets/space-3.webp";
import s4 from "@/assets/space-4.webp";
import addonCoffee from "@/assets/addon-coffee.webp";
import addonPhoto from "@/assets/addon-photo.webp";
import addonSound from "@/assets/addon-sound.webp";
import addonLinen from "@/assets/addon-linen.webp";
import { Money, Riyal } from "@/components/ui/riyal";

import { getSpace } from "@/lib/spaces-data";

const ORIGIN = "https://spayzo.lovable.app";

export const Route = createFileRoute("/spaces/$spaceId")({
  component: SpaceDetailPage,
  head: ({ params }) => {
    const space = getSpace(params.spaceId);
    const url = `${ORIGIN}/spaces/${params.spaceId}`;
    const image = `${ORIGIN}${space.img}`;
    return {
      meta: [
        { title: `${space.title} — Spayzo` },
        {
          name: "description",
          content: space.description,
        },
        { property: "og:title", content: `${space.title} — Spayzo` },
        { property: "og:description", content: space.description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: space.title,
            description: space.description,
            offers: {
              "@type": "Offer",
              priceCurrency: "SAR",
              price: String(space.price),
              availability: "https://schema.org/InStock",
              url,
            },
          }),
        },
      ],
    };
  },
});

const gallery = [s1, s2, s3, s4];

const amenities = [
  { icon: Wifi, label: "Fast Wi-Fi" },
  { icon: ParkingCircle, label: "Free parking" },
  { icon: Coffee, label: "Coffee bar" },
  { icon: Tv, label: "4K Display" },
  { icon: Snowflake, label: "Air conditioning" },
  { icon: Music2, label: "Sound system" },
  { icon: Users, label: "Up to 30 guests" },
  { icon: ShieldCheck, label: "Verified host" },
];

type AddonKind = "menu" | "rent";
type Addon = {
  id: string;
  name: string;
  description: string;
  pricePerHour: number; // for rent items: price per hour. For menu items: one-time unit price.
  image: string;
  kind: AddonKind;
};

const addons: Addon[] = [
  {
    id: "coffee",
    name: "Catering & Coffee Bar",
    description: "Barista-served coffee, pastries, and refreshments.",
    pricePerHour: 24,
    image: addonCoffee,
    kind: "menu",
  },
  {
    id: "linen",
    name: "Premium Linen Setup",
    description: "Fresh linens, table runners and ware.",
    pricePerHour: 12,
    image: addonLinen,
    kind: "menu",
  },
  {
    id: "photo",
    name: "Pro Photographer",
    description: "On-site photographer with edited deliverables.",
    pricePerHour: 80,
    image: addonPhoto,
    kind: "rent",
  },
  {
    id: "sound",
    name: "Sound & Mic Kit",
    description: "Wireless mics, mixer and powered speakers.",
    pricePerHour: 18,
    image: addonSound,
    kind: "rent",
  },
];

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8 → 21
const UNAVAILABLE = new Set([9, 10, 15, 19]);

const PRICE_PER_HOUR = 64;
const DEFAULT_MAX_GUESTS = 30;
// Per-space capacity — single-person spaces (e.g. private desks, phone booths) have 1.
const SPACE_CAPACITY: Record<string, number> = {
  "5": 1,
  "6": 1,
  "8": 1,
};
const getMaxGuests = (id: string) => SPACE_CAPACITY[id] ?? DEFAULT_MAX_GUESTS;

// Spaces that offer subscription upsell
const SPACES_WITH_SUBSCRIPTION = new Set(["1", "2", "3", "7"]);

function SpaceDetailPage() {
  const { t } = useTranslation();
  const { spaceId } = Route.useParams();
  const hasSubscription = SPACES_WITH_SUBSCRIPTION.has(spaceId);
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [selectedHours, setSelectedHours] = React.useState<number[]>([13, 14]);
  const [activeImage, setActiveImage] = React.useState(0);
  const [quantities, setQuantities] = React.useState<Record<string, number>>({});
  const [persons, setPersons] = React.useState(1);
  const MAX_GUESTS = getMaxGuests(spaceId);
  const isMultiPerson = MAX_GUESTS > 1;
  const { saved: isSaved, toggle: toggleSaved } = useFavorite(spaceId);


  const toggleHour = (h: number) => {
    if (UNAVAILABLE.has(h)) return;
    setSelectedHours((prev) =>
      prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h].sort((a, b) => a - b),
    );
  };

  const setQty = (id: string, delta: number) => {
    setQuantities((q) => {
      const next = Math.max(0, (q[id] ?? 0) + delta);
      return { ...q, [id]: next };
    });
  };

  const hoursCount = selectedHours.length;
  const spaceTotal = hoursCount * PRICE_PER_HOUR;
  const addonsTotal = addons.reduce(
    (sum, a) =>
      sum +
      (quantities[a.id] ?? 0) *
        a.pricePerHour *
        (a.kind === "rent" ? hoursCount : 1),
    0,
  );
  const subtotal = spaceTotal + addonsTotal;
  const DISCOUNT_RATE = hoursCount >= 3 ? 0.1 : 0;
  const discount = Math.round(subtotal * DISCOUNT_RATE);
  const serviceFee = Math.round((subtotal - discount) * 0.08);
  const VAT_RATE = 0.15;
  const vat = Math.round((subtotal - discount + serviceFee) * VAT_RATE);
  const total = subtotal - discount + serviceFee + vat;

  const timeRangeLabel =
    hoursCount === 0 ? "Select time" : formatHoursLabel(selectedHours);

  const checkoutSearch = {
    spaceId,
    date: date ? date.toISOString() : undefined,
    hours: selectedHours.join(","),
    addons: Object.entries(quantities)
      .filter(([, q]) => q > 0)
      .map(([id, q]) => `${id}:${q}`)
      .join(","),
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-7xl px-4 pb-32 pt-6 sm:px-6 lg:px-8 lg:pb-20">
        {/* Breadcrumb */}
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("space.backToSpaces")}
        </Link>

        {/* Title */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              {t("space.title")}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Star className="h-4 w-4 fill-foreground text-foreground" />
                <span className="font-medium text-foreground">4.92</span>
                <span>{t("space.reviewsCount", { count: 128 })}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-4 w-4" /> {t("space.location")}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full transition-transform active:scale-95 active:bg-accent"
              onClick={() => shareSpace({ title: t("space.title") })}
            >
              <Share2 className="h-4 w-4" /> {t("space.share")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full transition-transform active:scale-95 active:bg-accent"
              aria-pressed={isSaved}
              onClick={toggleSaved}
            >
              <Heart className={cn("h-4 w-4 transition-transform", isSaved && "scale-110 fill-primary text-primary")} />
              {isSaved ? t("space.saved") : t("space.save")}
            </Button>
          </div>
        </div>

        {/* Gallery */}
        <section className="mt-6 grid gap-3 sm:grid-cols-4">
          <div className="relative overflow-hidden rounded-3xl sm:col-span-3 sm:row-span-2">
            <img
              src={gallery[activeImage]}
              alt="Space main view"
              fetchPriority="high"
              decoding="async"
              className="aspect-[4/3] h-full w-full object-cover sm:aspect-[16/10]"
            />
          </div>
          {gallery.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveImage(i)}
              className={cn(
                "relative overflow-hidden rounded-2xl ring-offset-2 transition-all",
                activeImage === i
                  ? "ring-2 ring-primary"
                  : "opacity-90 hover:opacity-100",
              )}
            >
              <img
                src={img}
                alt={`Thumbnail ${i + 1}`}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] h-full w-full object-cover"
              />
            </button>
          ))}
        </section>

        {/* Two column */}
        <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* LEFT */}
          <div className="space-y-12">
            {/* Info */}
            <section>
              <h2 className="font-display text-2xl font-semibold">{t("space.about")}</h2>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
{t("space.aboutDesc")}
              </p>
            </section>

            {/* Host */}
            <section
              aria-label="Hosted by"
              className="flex flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-soft sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-border bg-card">
                  <img src={logo} alt="Loftworks Studios" className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("space.hostedBy")}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="font-display text-lg font-semibold">{t("space.hostName")}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">
                      <ShieldCheck className="h-3 w-3" /> {t("space.verified")}
                    </span>
                  </div>
                  <div className="mt-0.5 inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Star className="h-3 w-3 fill-foreground text-foreground" /> 4.89
                    </span>
                    <span>{t("space.hostingSince")}</span>
                  </div>
                </div>
              </div>
              <Button asChild variant="outline" className="rounded-full sm:shrink-0">
                <Link to="/companies/$companyId" params={{ companyId: "loftworks" }}>
                  {t("space.seeProfile")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                </Link>
              </Button>
            </section>

            {/* Vendor section hidden */}
            {false && <VendorSection />}


            {/* Tabs: Specification / Reviews / Location */}
            <section aria-label="Space details">
              <Tabs defaultValue="specification" className="w-full">
                <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-full bg-muted p-1">
                  <TabsTrigger value="specification" className="rounded-full px-4 py-2">
                    {t("space.tabs.specification")}
                  </TabsTrigger>
                  <TabsTrigger value="reviews" className="rounded-full px-4 py-2">
                    {t("space.tabs.reviews")}
                  </TabsTrigger>
                  <TabsTrigger value="location" className="rounded-full px-4 py-2">
                    {t("space.tabs.location")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="specification" className="mt-6 space-y-6">
                  <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[
                      { icon: Ruler, label: t("space.specs.area"), value: t("space.specs.areaValue") },
                      { icon: Users, label: t("space.specs.capacity"), value: t("space.specs.capacityValue") },
                      { icon: DoorOpen, label: t("space.specs.entrances"), value: t("space.specs.entrancesValue") },
                      { icon: Maximize2, label: t("space.specs.ceiling"), value: t("space.specs.ceilingValue") },
                    ].map((s) => (
                      <li
                        key={s.label}
                        className="rounded-2xl border border-border bg-card p-3 shadow-soft"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-soft text-primary">
                          <s.icon className="h-4 w-4" />
                        </span>
                        <div className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                          {s.label}
                        </div>
                        <div className="mt-0.5 font-display text-sm font-semibold">{s.value}</div>
                      </li>
                    ))}
                  </ul>

                  <div>
                    <h3 className="font-display text-lg font-semibold">{t("space.whatOffers")}</h3>
                    <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-2">
                      {amenities.map((a) => (
                        <li
                          key={a.label}
                          className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-sm shadow-soft"
                        >
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-soft text-primary">
                            <a.icon className="h-4 w-4" />
                          </span>
                          {t(`space.amenities.${a.label}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent value="reviews" className="mt-6">
                  <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
                    <div className="rounded-3xl border border-border bg-card p-5 text-center shadow-soft lg:sticky lg:top-24 lg:self-start">
                      <div className="font-display text-4xl font-bold">4.92</div>
                      <div className="mt-1 inline-flex items-center gap-0.5 text-primary">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5 fill-current" />
                        ))}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{t("space.reviewsCount", { count: 128 })}</div>
                    </div>
                    <div className="space-y-4">
                      <ReviewComposer spaceId={spaceId} />
                      <ReviewsList />
                    </div>
                  </div>
                </TabsContent>


                <TabsContent value="location" className="mt-6 space-y-4">
                  <div className="overflow-hidden rounded-3xl border border-border shadow-soft">
                    <iframe
                      title="Space location map"
                      src="https://www.openstreetmap.org/export/embed.html?bbox=-73.9712%2C40.7075%2C-73.9412%2C40.7275&amp;layer=mapnik&amp;marker=40.7175%2C-73.9562"
                      className="h-[320px] w-full border-0 sm:h-[400px]"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-col gap-3 rounded-3xl border border-border bg-card p-5 shadow-soft sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                        <MapPin className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="font-display text-base font-semibold">
                          {t("space.location")}
                        </div>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {t("space.locationAddress")}
                        </p>
                      </div>
                    </div>
                    <Button asChild variant="outline" className="rounded-full sm:shrink-0">
                      <a
                        href="https://www.openstreetmap.org/?mlat=40.7175&mlon=-73.9562#map=15/40.7175/-73.9562"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Navigation className="h-4 w-4" /> {t("space.openInMaps")}
                      </a>
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </section>

            {/* Time selection */}
            <section>
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-semibold">{t("space.pickTime")}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("space.pickTimeDesc")}
                  </p>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="rounded-full">
                      <CalendarIcon className="h-4 w-4" />
                      {date ? format(date, "EEE, MMM d") : t("discover.pickDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-7">
                {HOURS.map((h) => {
                  const disabled = UNAVAILABLE.has(h);
                  const selected = selectedHours.includes(h);
                  return (
                    <button
                      key={h}
                      onClick={() => toggleHour(h)}
                      disabled={disabled}
                      className={cn(
                        "group flex flex-col items-center justify-center rounded-2xl border px-3 py-3 text-sm font-medium transition-all",
                        disabled &&
                          "cursor-not-allowed border-dashed border-border bg-muted/40 text-muted-foreground/60 line-through",
                        !disabled &&
                          !selected &&
                          "border-border bg-card hover:border-primary/50 hover:bg-primary-soft",
                        selected &&
                          "border-primary bg-primary text-primary-foreground shadow-soft",
                      )}
                    >
                      <Clock className="mb-1 h-3.5 w-3.5 opacity-70" />
                      {formatHour(h)}
                    </button>
                  );
                })}
              </div>
              {hoursCount > 0 && (
                <p className="mt-4 text-sm text-muted-foreground">
                  {t("space.selected")} <span className="font-medium text-foreground">{timeRangeLabel}</span>
                  {" "}· {hoursCount} {hoursCount > 1 ? t("space.hour_other") : t("space.hour_one")}
                </p>
              )}

              {/* Number of persons (only for multi-person spaces) */}
              {isMultiPerson && (
                <div className="mt-6 flex flex-col gap-3 rounded-3xl border border-border bg-card p-4 shadow-soft sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                      <Users className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-display text-base font-semibold">{t("space.numberOfPersons")}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("space.capacityUpTo", { count: MAX_GUESTS })}
                      </div>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 self-start rounded-full border border-border bg-card p-1 sm:self-auto">
                    <button
                      aria-label={t("space.decreasePersons")}
                      onClick={() => setPersons((p) => Math.max(1, p - 1))}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">{persons}</span>
                    <button
                      aria-label={t("space.increasePersons")}
                      onClick={() => setPersons((p) => Math.min(MAX_GUESTS, p + 1))}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Add-ons with Menu / Rent tabs */}
            <section>
              <h2 className="font-display text-2xl font-semibold">{t("space.enhanceBooking")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("space.enhanceDesc", { menu: t("space.menu"), rent: t("space.rent") })
                  .split(/<menu>|<\/menu>|<rent>|<\/rent>/)
                  .map((part, i) => (i === 1 ? <span key={i} className="font-medium text-foreground">{t("space.menu")}</span> : i === 3 ? <span key={i} className="font-medium text-foreground">{t("space.rent")}</span> : <React.Fragment key={i}>{part}</React.Fragment>))}
              </p>

              <Tabs defaultValue="menu" className="mt-6 w-full">
                <TabsList className="h-auto w-full justify-start gap-1 rounded-full bg-muted p-1 sm:w-auto">
                  <TabsTrigger value="menu" className="rounded-full px-4 py-2">
                    {t("space.menuBuy")}
                  </TabsTrigger>
                  <TabsTrigger value="rent" className="rounded-full px-4 py-2">
                    {t("space.rentEquipment")}
                  </TabsTrigger>
                </TabsList>

                {(["menu", "rent"] as const).map((kind) => {
                  const list = addons.filter((a) => a.kind === kind);
                  return (
                    <TabsContent key={kind} value={kind} className="mt-4">
                      <ul className="space-y-3">
                        {list.map((a) => {
                          const qty = quantities[a.id] ?? 0;
                          const multiplier = a.kind === "rent" ? hoursCount : 1;
                          const lineTotal = qty * a.pricePerHour * multiplier;
                          const unitSuffix = a.kind === "rent" ? t("space.perHourShort") : t("space.perItem");
                          return (
                            <li
                              key={a.id}
                              className={cn(
                                "flex items-center gap-4 rounded-3xl border bg-card p-3 shadow-soft transition-colors",
                                qty > 0 ? "border-primary/40 bg-primary-soft/40" : "border-border",
                              )}
                            >
                              <img
                                src={a.image}
                                alt={t(`space.addons.${a.id}.name`, { defaultValue: a.name })}
                                loading="lazy"
                decoding="async"
                                className="h-20 w-20 shrink-0 rounded-2xl object-cover sm:h-24 sm:w-24"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                  <h3 className="font-display text-base font-semibold leading-snug">
                                    {t(`space.addons.${a.id}.name`, { defaultValue: a.name })}
                                  </h3>
                                  <div className="text-end">
                                    <div className="font-display text-base font-semibold"><Riyal /> {a.pricePerHour}
                                      <span className="text-xs font-normal text-muted-foreground">
                                        {unitSuffix}
                                      </span>
                                    </div>
                                    {qty > 0 && (
                                      <div className="text-xs text-muted-foreground">
                                        {t("space.plusTotal", { amount: lineTotal })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">
                                  {t(`space.addons.${a.id}.description`, { defaultValue: a.description })}
                                </p>
                                <div className="mt-3 flex items-center justify-between">
                                  {qty === 0 ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="rounded-full"
                                      onClick={() => setQty(a.id, 1)}
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                      {a.kind === "rent" ? t("space.rent") : t("space.add")}
                                    </Button>
                                  ) : (
                                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card p-1">
                                      <button
                                        aria-label={t("space.decrease")}
                                        onClick={() => setQty(a.id, -1)}
                                        className="flex h-7 w-7 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted"
                                      >
                                        <Minus className="h-3.5 w-3.5" />
                                      </button>
                                      <span className="w-5 text-center text-sm font-medium">
                                        {qty}
                                      </span>
                                      <button
                                        aria-label={t("space.increase")}
                                        onClick={() => setQty(a.id, 1)}
                                        className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90"
                                      >
                                        <Plus className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </section>

            {/* Subscription upsell */}
            {hasSubscription && <SubscriptionUpsell spaceTotal={spaceTotal} />}

          </div>


          {/* RIGHT — Sticky booking card */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <BookingCard
                date={date}
                hoursCount={hoursCount}
                timeRangeLabel={timeRangeLabel}
                spaceTotal={spaceTotal}
                addonsTotal={addonsTotal}
                discount={discount}
                serviceFee={serviceFee}
                vat={vat}
                vatRate={VAT_RATE}
                total={total}
                addons={addons}
                quantities={quantities}
                checkoutSearch={checkoutSearch}
              />
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile bottom CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 p-3 backdrop-blur-xl shadow-elevated lg:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-1">
          <div>
            <div className="font-display text-lg font-bold leading-none"><Riyal /> {total}
              <span className="text-xs font-normal text-muted-foreground"> total</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {hoursCount > 0 ? `${hoursCount}h · ${timeRangeLabel}` : t("space.selectTime")}
            </div>
          </div>
          <Button
            asChild
            size="lg"
            disabled={hoursCount === 0}
            className="rounded-full px-6 shadow-glow"
          >
            <Link to="/checkout" search={checkoutSearch}>{t("space.bookNow")}</Link>
          </Button>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

function BookingCard({
  date,
  hoursCount,
  timeRangeLabel,
  spaceTotal,
  addonsTotal,
  discount,
  serviceFee,
  vat,
  vatRate,
  total,
  addons,
  quantities,
  checkoutSearch,
}: {
  date: Date | undefined;
  hoursCount: number;
  timeRangeLabel: string;
  spaceTotal: number;
  addonsTotal: number;
  discount: number;
  serviceFee: number;
  vat: number;
  vatRate: number;
  total: number;
  addons: Addon[];
  quantities: Record<string, number>;
  checkoutSearch: { spaceId: string; date?: string; hours: string; addons: string };
}) {
  const { t } = useTranslation();
  const activeAddons = addons.filter((a) => (quantities[a.id] ?? 0) > 0);
  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-elevated">
      <div className="border-b border-border bg-surface p-5">
        <div className="flex items-baseline justify-between">
          <div>
            <span className="font-display text-2xl font-bold"><Riyal /> {PRICE_PER_HOUR}</span>
            <span className="text-sm text-muted-foreground"> /hour</span>
          </div>
          <div className="inline-flex items-center gap-1 text-sm">
            <Star className="h-3.5 w-3.5 fill-foreground text-foreground" />
            <span className="font-medium">4.92</span>
            <span className="text-muted-foreground">· 128</span>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Date" value={date ? format(date, "MMM d, yyyy") : "—"} />
          <Field label="Time" value={hoursCount ? timeRangeLabel : "—"} />
        </div>

        <div className="space-y-2 border-t border-border pt-4 text-sm">
          <Row
            label={<><Money value={PRICE_PER_HOUR} /> × {hoursCount} hour{hoursCount === 1 ? "" : "s"}</>}
            value={<Money value={spaceTotal} />}
          />

          {activeAddons.length > 0 && (
            <div className="space-y-1.5 rounded-2xl bg-surface p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Add-ons
              </div>
              {activeAddons.map((a) => {
                const qty = quantities[a.id] ?? 0;
                const mult = a.kind === "rent" ? hoursCount : 1;
                const suffix = a.kind === "rent" ? ` · ${hoursCount}h` : "";
                return (
                  <Row
                    key={a.id}
                    label={`${a.name} × ${qty}${suffix}`}
                    value={<Money value={qty * a.pricePerHour * mult} />}
                    muted
                  />
                );
              })}
            </div>
          )}

          {discount > 0 && (
            <Row
              label={
                <span className="inline-flex items-center gap-1 text-primary">
                  <BadgePercent className="h-3.5 w-3.5" /> Discount (3h+)
                </span>
              }
              value={<Money value={"-" + (discount)} />}
              accent
            />
          )}
          <Row label="Service fee" value={<Money value={serviceFee} />} muted />
          <Row label={`VAT (${Math.round(vatRate * 100)}%)`} value={<Money value={vat} />} muted />
        </div>

        <div className="flex items-baseline justify-between border-t border-border pt-4">
          <span className="font-display text-base font-semibold">Total</span>
          <span className="font-display text-2xl font-bold"><Riyal /> {total}</span>
        </div>

        <Button
          asChild
          size="lg"
          disabled={hoursCount === 0}
          className="w-full rounded-full shadow-glow"
        >
          <Link to="/checkout" search={checkoutSearch}>{t("space.bookNow")}</Link>
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          You won&apos;t be charged yet
        </p>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 truncate text-sm font-medium">{value}</div>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  accent,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  muted?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span
        className={cn(
          "truncate",
          accent
            ? "text-primary"
            : muted
              ? "text-muted-foreground"
              : "text-foreground",
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "font-medium",
          accent
            ? "text-primary"
            : muted
              ? "text-muted-foreground"
              : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function formatHour(h: number) {
  const hour = h % 24;
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display} ${suffix}`;
}

export function formatHoursLabel(hours: number[]) {
  if (!hours.length) return "";
  const sorted = [...hours].sort((a, b) => a - b);
  const runs: Array<[number, number]> = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) {
      prev = sorted[i];
    } else {
      runs.push([start, prev]);
      start = sorted[i];
      prev = sorted[i];
    }
  }
  runs.push([start, prev]);
  return runs
    .map(([s, e]) => `${formatHour(s)} – ${formatHour(e + 1)}`)
    .join(" · ");
}

/* ------------ Vendor (owner-curated services / menu) ------------ */

// Owner toggle from merchant settings — when false, section is hidden.
const VENDOR_ENABLED = true;

type VendorKind = "menu" | "rent";
type VendorItem = {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  kind: VendorKind;
  tag?: string;
};

const VENDOR = {
  name: "Loftworks Café & Services",
  tagline: "Curated by your host — order alongside your booking.",
  items: [
    {
      id: "v-coffee",
      name: "Specialty Coffee Flight",
      description: "Three signature espresso-based drinks brewed on-site.",
      price: 14,
      image: addonCoffee,
      kind: "menu",
      tag: "Bestseller",
    },
    {
      id: "v-plates",
      name: "Mediterranean Sharing Plate",
      description: "Hummus, olives, warm pita, marinated vegetables.",
      price: 28,
      image: addonLinen,
      kind: "menu",
    },
    {
      id: "v-photo",
      name: "30-min Branding Photoshoot",
      description: "In-house photographer, 10 edited shots delivered same day.",
      price: 95,
      image: addonPhoto,
      kind: "rent",
      tag: "New",
    },
    {
      id: "v-sound",
      name: "Live DJ Setup",
      description: "Resident DJ with full equipment for up to 2 hours.",
      price: 220,
      image: addonSound,
      kind: "rent",
    },
  ] as VendorItem[],
};

function VendorSection() {
  if (!VENDOR_ENABLED) return null;
  return (
    <section aria-label="Vendor offerings">
      <div className="flex items-end justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
            <Store className="h-5 w-5" />
          </span>
          <div>
            <h2 className="font-display text-2xl font-semibold">
              {VENDOR.name}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{VENDOR.tagline}</p>
          </div>
        </div>
        <span className="hidden shrink-0 items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground sm:inline-flex">
          <UtensilsCrossed className="h-3 w-3" /> Offered by host
        </span>
      </div>

      <Tabs defaultValue="menu" className="mt-6 w-full">
        <TabsList className="h-auto w-full justify-start gap-1 rounded-full bg-muted p-1 sm:w-auto">
          <TabsTrigger value="menu" className="rounded-full px-4 py-2">
            Menu · Buy
          </TabsTrigger>
          <TabsTrigger value="rent" className="rounded-full px-4 py-2">
            Rent · Equipment
          </TabsTrigger>
        </TabsList>

        {(["menu", "rent"] as const).map((kind) => {
          const list = VENDOR.items.filter((i) => i.kind === kind);
          return (
            <TabsContent key={kind} value={kind} className="mt-4">
              <ul className="grid gap-3 sm:grid-cols-2">
                {list.map((item) => {
                  const unitSuffix = item.kind === "rent" ? "/h" : "/item";
                  return (
                    <li
                      key={item.id}
                      className="group flex items-center gap-4 rounded-3xl border border-border bg-card p-3 shadow-soft transition-colors hover:border-primary/40"
                    >
                      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl sm:h-24 sm:w-24">
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                decoding="async"
                          className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        />
                        {item.tag && (
                          <span className="absolute start-1.5 top-1.5 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-soft">
                            {item.tag}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-display text-base font-semibold leading-snug">
                            {item.name}
                          </h3>
                          <div className="text-end font-display text-base font-semibold"><Riyal /> {item.price}
                            <span className="text-xs font-normal text-muted-foreground">
                              {unitSuffix}
                            </span>
                          </div>
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">
                          {item.description}
                        </p>
                        <div className="mt-3">
                          <Button size="sm" variant="outline" className="rounded-full">
                            <Plus className="h-3.5 w-3.5" />
                            {item.kind === "rent" ? "Rent" : "Add"}
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </TabsContent>
          );
        })}
      </Tabs>
    </section>
  );
}

type PlanId = "basic" | "pro" | "premium";
type Plan = {
  id: PlanId;
  name: string;
  price: number;
  discount: number; // percent
  perk?: string;
  best?: boolean;
};

const PLANS: Plan[] = [
  { id: "basic", name: "Basic", price: 9, discount: 5 },
  { id: "pro", name: "Pro", price: 29, discount: 15, perk: "Priority support included", best: true },
  { id: "premium", name: "Premium", price: 99, discount: 25 },
];

function SubscriptionUpsell({ spaceTotal }: { spaceTotal: number }) {
  const [selected, setSelected] = React.useState<PlanId | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = React.useState<string>("");
  const [toDate, setToDate] = React.useState<string>("");

  const rangeValid = React.useMemo(() => {
    if (!fromDate || !toDate) return false;
    const f = new Date(fromDate).getTime();
    const t = new Date(toDate).getTime();
    if (Number.isNaN(f) || Number.isNaN(t) || t <= f) return false;
    const days = (t - f) / (1000 * 60 * 60 * 24);
    return days >= 28 && days <= 31; // fixed one-month range
  }, [fromDate, toDate]);

  // Reset selection if range becomes invalid
  React.useEffect(() => {
    if (!rangeValid && selected) setSelected(null);
  }, [rangeValid, selected]);

  const active = PLANS.find((p) => p.id === selected) ?? null;
  const savings = active ? Math.round(spaceTotal * active.discount) / 100 : 0;

  return (
    <section className="overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary-soft/60 via-background to-primary-soft/30 p-5 shadow-soft sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold leading-tight sm:text-2xl">
              Save more on this booking
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Applies to the space price only (not add-ons).{" "}
              <span className="ms-1 inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground">
                <CircleCheck className="h-3 w-3" /> Cancel anytime
              </span>
            </p>
          </div>
        </div>
        <span className="rounded-md border border-border bg-card px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Optional
        </span>
      </div>

      {/* Fixed monthly range (required before selecting a plan) */}
      <div className="mt-5 rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-foreground">Subscription period</p>
            <p className="text-[11px] text-muted-foreground">
              Choose a fixed one-month range (from → to) before selecting a plan.
            </p>
          </div>
          {rangeValid ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              <CircleCheck className="h-3 w-3" /> Valid range
            </span>
          ) : (
            <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              Required
            </span>
          )}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-foreground">From</span>
            <input
              type="date"
              min={today}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-foreground">To</span>
            <input
              type="date"
              min={fromDate || today}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            />
          </label>
        </div>
        {fromDate && toDate && !rangeValid && (
          <p className="mt-2 text-[11px] text-destructive">
            Range must be a fixed one month (28–31 days).
          </p>
        )}
      </div>

      <div className={cn("mt-5 grid gap-3 sm:grid-cols-3", !rangeValid && "opacity-60")}>
        {PLANS.map((p) => {
          const isActive = selected === p.id;
          return (
            <button
              key={p.id}
              type="button"
              disabled={!rangeValid}
              onClick={() => setSelected(isActive ? null : p.id)}
              className={cn(
                "relative rounded-2xl border bg-card p-4 text-start transition-all",
                isActive
                  ? "border-primary ring-2 ring-primary/40 shadow-elevated"
                  : "border-border hover:border-primary/40",
                !rangeValid && "cursor-not-allowed hover:border-border",
              )}
            >
              {p.best && (
                <span className="absolute -top-2.5 start-1/2 -translate-x-1/2 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-soft">
                  Best value
                </span>
              )}
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-lg font-bold">{p.name}</h3>
                <span
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full border-2",
                    isActive ? "border-primary bg-primary" : "border-border bg-card",
                  )}
                >
                  {isActive && <span className="h-2 w-2 rounded-full bg-primary-foreground" />}
                </span>
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-2xl font-bold"><Riyal /> {p.price}</span>
                <span className="text-xs text-muted-foreground">/mo</span>
              </div>
              <div
                className={cn(
                  "mt-3 flex items-start gap-1.5 text-xs",
                  isActive ? "text-foreground" : "text-muted-foreground",
                )}
              >
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{p.discount}% off space price</span>
              </div>
              {p.perk && isActive && (
                <p className="mt-1 ps-5 text-[11px] text-muted-foreground">{p.perk}</p>
              )}
            </button>
          );
        })}
      </div>

      {!rangeValid && (
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          Select a fixed one-month range above to enable plans.
        </p>
      )}

      {active && rangeValid && spaceTotal > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm text-primary">
          <CircleCheck className="h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">{active.name} plan applied.</span> You save $
            {savings.toFixed(2)} on the space price!
          </span>
        </div>
      )}
    </section>
  );
}


function ReviewComposer({ spaceId }: { spaceId: string }) {
  const booked = useHasBooked(spaceId);
  const [name, setName] = React.useState("");
  const [rating, setRating] = React.useState(5);
  const [hover, setHover] = React.useState(0);
  const [text, setText] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!booked) {
    return (
      <div className="rounded-3xl border border-dashed border-border bg-card/50 p-5 text-sm text-muted-foreground shadow-soft">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Star className="h-4 w-4" />
          </span>
          <div>
            <div className="font-display text-sm font-semibold text-foreground">
              Book this space to leave a review
            </div>
            <p className="mt-1">
              Only guests who&apos;ve completed a booking can share a review.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const trimmedText = text.trim();
    if (trimmedName.length < 2 || trimmedName.length > 60) {
      setError("Please enter your name (2–60 characters).");
      return;
    }
    if (trimmedText.length < 10 || trimmedText.length > 500) {
      setError("Review must be between 10 and 500 characters.");
      return;
    }
    if (rating < 1 || rating > 5) {
      setError("Please choose a rating.");
      return;
    }
    addReview({ spaceId, name: trimmedName, rating, text: trimmedText });
    setName("");
    setText("");
    setRating(5);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2500);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-3xl border border-border bg-card p-5 shadow-soft"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-display text-base font-semibold">Write a review</div>
        <div
          className="inline-flex items-center gap-1"
          onMouseLeave={() => setHover(0)}
        >
          {Array.from({ length: 5 }).map((_, i) => {
            const value = i + 1;
            const active = (hover || rating) >= value;
            return (
              <button
                key={value}
                type="button"
                aria-label={`${value} star${value > 1 ? "s" : ""}`}
                onMouseEnter={() => setHover(value)}
                onClick={() => setRating(value)}
                className="p-0.5 transition-transform active:scale-90"
              >
                <Star
                  className={cn(
                    "h-5 w-5",
                    active ? "fill-primary text-primary" : "text-muted-foreground/40",
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        maxLength={60}
        className="rounded-2xl"
      />
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Share your experience with this space…"
        rows={3}
        maxLength={500}
        className="rounded-2xl"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      {submitted && (
        <p className="text-xs text-primary">Thanks — your review has been posted.</p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{text.length}/500</span>
        <Button type="submit" className="rounded-full">
          Post review
        </Button>
      </div>
    </form>
  );
}

function ReviewsList() {
  const { spaceId } = Route.useParams();
  const userReviews = useSpaceReviews(spaceId);
  const seed = [
    {
      id: "seed-1",
      name: "Maya R.",
      initials: "MR",
      rating: 5,
      date: "March 2026",
      text: "Beautiful natural light all afternoon — exactly what we needed for the shoot.",
    },
    {
      id: "seed-2",
      name: "Daniel P.",
      initials: "DP",
      rating: 5,
      date: "February 2026",
      text: "Easy check-in and the host was responsive throughout. Will rebook.",
    },
    {
      id: "seed-3",
      name: "Aisha K.",
      initials: "AK",
      rating: 4,
      date: "January 2026",
      text: "Lovely space. Street parking can be tight on Saturdays — Uber recommended.",
    },
  ];
  const items = [
    ...userReviews
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((r) => ({
        id: r.id,
        name: r.name,
        initials: r.initials,
        rating: r.rating,
        date: r.date,
        text: r.text,
      })),
    ...seed,
  ];
  return (
    <ul className="space-y-3">
      {items.map((r) => (
        <li key={r.id} className="rounded-3xl border border-border bg-card p-4 shadow-soft">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary">
              {r.initials}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                <span className="font-display text-sm font-semibold">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.date}</span>
              </div>
              <div className="mt-0.5 inline-flex items-center gap-0.5 text-primary">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={cn("h-3 w-3", i < r.rating ? "fill-current" : "opacity-30")}
                  />
                ))}
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{r.text}</p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
